import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { demo, StatusBadge, SyntheticTag } from "./c1lib";
import { Section } from "./PublicLayout";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { ShieldCheck, ShieldX, ArrowRight, Lock, FileCheck2 } from "lucide-react";

export default function Validation() {
  const [rows, setRows] = useState([]);
  const [rid, setRid] = useState(null);
  const [d, setD] = useState(null);

  const loadList = useCallback(() => { demo.list().then(setRows).catch(() => {}); }, []);
  const loadRec = useCallback(() => { if (rid) demo.record(rid).then(setD).catch(() => {}); }, [rid]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadRec(); }, [loadRec]);

  const pending = rows.filter((r) => ["VALIDATION_PENDING", "EVIDENCE_RECEIVED"].includes(r.status) && !r.is_reference);

  return (
    <Section title="Validation" subtitle="The DEMO AUTHORISED VALIDATOR reviews evidence against the temple's required-evidence rules and decides. Certification is earned — it can be withheld.">
      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-6 text-xs text-slate-500 font-mono uppercase tracking-wider inline-flex items-center gap-2" data-testid="validator-identity">
        <ShieldCheck size={14} /> Acting as DEMO AUTHORISED VALIDATOR
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5">
          <h3 className="font-mono text-[11px] uppercase tracking-wider text-slate-500 mb-3">Awaiting validation</h3>
          <div className="border border-slate-200 bg-white divide-y divide-slate-100" data-testid="validation-list">
            {pending.length === 0 && <div className="p-6 text-slate-400 text-sm">Nothing awaiting validation. Submit a record from the Temple Manager console.</div>}
            {pending.map((r) => (
              <button key={r.record_id} onClick={() => setRid(r.record_id)} data-testid={`validation-row-${r.record_id}`}
                className={`w-full text-left px-5 py-3 hover:bg-slate-50 flex items-center gap-3 ${rid === r.record_id ? "bg-slate-50" : ""}`}>
                <div className="flex-1 min-w-0"><div className="font-mono text-[11px] text-slate-600">{r.record_id}</div><div className="text-sm text-slate-700 truncate">{r.seva_name}</div></div>
                <StatusBadge status={r.status} />
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3">Reference cases are locked; run a fresh journey to validate here.</p>
        </div>
        <div className="lg:col-span-7">
          {!d ? <div className="border border-dashed border-slate-300 rounded-sm p-10 text-center text-slate-400 text-sm">Select a record to validate.</div>
            : <ValidatePanel d={d} onDone={() => { loadList(); loadRec(); }} />}
        </div>
      </div>
    </Section>
  );
}

function ValidatePanel({ d, onDone }) {
  const r = d.record;
  const required = r.seva_snapshot?.evidence_required || [];
  const [accepted, setAccepted] = useState({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const locked = r.is_reference;

  const toggle = (id) => setAccepted((a) => ({ ...a, [id]: !a[id] }));
  const acceptedIds = Object.keys(accepted).filter((k) => accepted[k]);
  const acceptedTypes = new Set(d.evidence.filter((e) => accepted[e.id]).map((e) => e.type));
  const missing = required.filter((t) => !acceptedTypes.has(t));

  const decide = async (decision) => {
    setBusy(true);
    try {
      const res = await demo.validate(r.record_id, { decision, reason, accepted_evidence_ids: acceptedIds });
      if (res.status === "CERTIFIED") toast.success(`Certified. Integrity hash generated (v${res.version}).`);
      else toast.warning(`Certification withheld. ${res.missing_required?.length ? "Missing: " + res.missing_required.join(", ") : ""}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Validation failed");
    } finally { setBusy(false); }
  };

  if (locked) return <div className="border border-slate-200 bg-slate-50 rounded-sm p-6 text-sm text-slate-500">Locked reference record — cannot be re-validated.</div>;

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="validate-panel">
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <span className="font-mono text-sm text-slate-700 bg-slate-100 px-2 py-0.5 rounded-sm">{r.record_id}</span>
        <StatusBadge status={r.status} />
        <Link to={`/records/${r.record_id}`} className="ml-auto text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">Verified Record <ArrowRight size={13} /></Link>
      </div>
      <div className="text-[11px] font-mono text-slate-400 mb-4">Required evidence: {required.join(", ")}</div>

      <div className="space-y-2 mb-4" data-testid="validate-evidence">
        {d.evidence.length === 0 && <p className="text-sm text-slate-400">No evidence submitted — certification cannot be granted.</p>}
        {d.evidence.map((e) => (
          <label key={e.id} className="flex items-start gap-3 border border-slate-200 rounded-sm p-3 cursor-pointer hover:border-slate-400" data-testid={`accept-evidence-${e.type}`}>
            <Checkbox checked={!!accepted[e.id]} onCheckedChange={() => toggle(e.id)} className="mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2"><FileCheck2 size={14} className="text-slate-500" /><span className="font-mono text-xs text-slate-700">{e.type}</span><span className="font-mono text-[9px] text-slate-400">{e.hash_type}</span></div>
              <div className="text-[10px] font-mono text-slate-400 break-all mt-1">sha256: {e.sha256}</div>
            </div>
          </label>
        ))}
      </div>

      {missing.length > 0 && (
        <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 mb-4" data-testid="missing-required">
          Required evidence not yet accepted: <strong>{missing.join(", ")}</strong>. Certification will be withheld.
        </div>
      )}

      <div>
        <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Decision reason</Label>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5 rounded-sm border-slate-300 min-h-[60px]" data-testid="validate-reason" />
      </div>
      <div className="flex gap-3 mt-4">
        <Button disabled={busy} onClick={() => decide("VALIDATE")} className="rounded-sm bg-emerald-700 hover:bg-emerald-800 gap-1.5" data-testid="btn-validate"><ShieldCheck size={15} /> Validate & Certify</Button>
        <Button disabled={busy} onClick={() => decide("WITHHOLD")} variant="outline" className="rounded-sm gap-1.5 border-slate-400 text-slate-700" data-testid="btn-withhold"><ShieldX size={15} /> Withhold Certification</Button>
      </div>
      <p className="text-[11px] text-slate-400 mt-3">A validate decision still results in withheld certification if any required evidence type is not accepted — evidence is not automatically truth.</p>
    </div>
  );
}
